import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataGrid } from '@p81-common/ui';
import type { DataGridProps, DropDownMenuProps } from '@p81-common/ui';
import { useEdgeDataGrid } from '../helpers/useEdgeDataGrid';
import styles from '../styles.module.scss';

interface EdgeDataGridProps {
  useDataGridResultProps: ReturnType<typeof useEdgeDataGrid>;
}

const EdgeDataGrid = ({ useDataGridResultProps }: EdgeDataGridProps) => {
  const { t } = useTranslation('edges');

  const {
    handleTableSettingsMenuItemClick,
    handleSearchChangeCallback,
    handleChangeNumOfRows,
    setFilter,
    filter,
    onSortEnd,
    rowSelectionChanged,
    tableSettingsDisplay,
    changeCurrentPage,
    numOfRowsOptions,
    tableSettingsChanged,
    results,
    tableData,
    tableConfigDisplay,
    loaderTable,
    direction,
    setDirection,
    startFilter,
    setStartFilter
  } = useDataGridResultProps;

  const onChangeNumOfRows: DropDownMenuProps['handleClick'] = useCallback(
    itemsPerPage => () => {
      (handleChangeNumOfRows(itemsPerPage) as () => void)();
      setFilter({ ...filter, itemsPerPage: Number(itemsPerPage) });
    },
    [filter, handleChangeNumOfRows, setFilter]
  );

  const handleSortable = (field: any) => {
    setDirection(direction === 1 ? -1 : 1);
    setFilter({
      ...filter,
      fieldName: field
    });
    setStartFilter(!startFilter);
  };

  const paginationProps: DataGridProps['paginationProps'] = useMemo(
    () => ({
      page: filter.currentPage - 1,
      count: results?.totalRecords || 0,
      rowsPerPage:
        numOfRowsOptions.find(
          (item: { name: number; selected: boolean }) => item.selected === true
        )?.name ?? 25,
      onChangePage: (e, page) => {
        setFilter({ ...filter, currentPage: page + 1 });
        changeCurrentPage(page + 1);
      },
      hideNavigation: !results?.totalRecords,
      hideRowCount: !results?.totalRecords
    }),
    [
      changeCurrentPage,
      filter,
      numOfRowsOptions,
      results?.totalRecords,
      setFilter
    ]
  );

  const tableDataDisplay = useMemo(
    () => ({
      page: results?.page || 0,
      totalPages: results?.totalPages || 0,
      totalRecords: results?.totalRecords || 0,
      data: tableData || []
    }),
    [results, tableData]
  );

  return (
    <DataGrid
      classes={{
        container: styles.wrapDG
      }}
      autoHeight={true}
      tableConfig={tableConfigDisplay}
      tableData={tableDataDisplay}
      lazyLoading={true}
      loading={loaderTable}
      numOfRowsOptions={numOfRowsOptions}
      rowCount={results?.totalRecords || 0}
      getRowId={(row: any) => row.edge_id}
      paginationProps={paginationProps}
      toolbarProps={{
        title1: t('EDGES_LIST.ALL_RESULTS_TITLE'),
        title2: `(${results?.totalRecords || 0} Edges)`,
        tableSettingsListItems: tableSettingsDisplay,
        rowSelectionChanged,
        tableSettingsChanged,
        handleTableSettingsMenuItemClick,
        handleSearchChangeCallback,
        onChangeNumOfRows,
        onSortEnd
      }}
      onSortModelChange={a => {
        handleSortable(a[0].field);
      }}
      initialState={{
        pinnedColumns: {
          left: [tableConfigDisplay[0]?.field]
        }
      }}
      sortingMode="server"
      paginationMode="server"
    />
  );
};

export default memo(EdgeDataGrid);
